import React, { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { createSearchParams, Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
	IOrderInfoDetails,
	IOrderInfoResponse,
	Locales,
	OfferContext,
	OrderService,
	TollService,
	VignetteContext,
	getErrorTextFromResponse,
	getMangeOrderCollection,
	getOrderEntriesSortedByModuleType,
	getOrderUrlParams,
	getResponseErrorNumber,
	setRouteLangParam,
	getLabelValue,
	ITypeVignettesOrderManagementFields,
	getUrlBasePath,
} from '@adac-mp/shared';
import {
	Button,
	ErrorCrossIcon,
	Head,
	SkeletonTemplate,
	Toast,
	Wrapper,
	withLocalization,
	ContentLoader,
	Typography,
	Edit,
} from '@adac-mp/components';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { ProductOrderSummarySection } from '../components/vignette/productOrderSummarySection';
import { renderTeasers } from '../utils/renderTeasers';

export const OrderManagementPage = () => {
	const navigate = useNavigate();
	const { lang } = useParams();
	const { viewport } = useViewport();
	const { orderData, contentfulLabels, updateNewOrderData, updateContentfulLabels } = useContext(VignetteContext);
	const { labels, orderList, updateAllCountries, updateOrderList } = useContext(OfferContext);
	const [orderInfoDetailsByModule, setOrderInfoDetailsByModule] = useState<
		[string, IOrderInfoDetails[]][] | undefined
	>(undefined);
	const [dataLoading, setDataLoading] = useState(false);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const handleOrderException = (res: IOrderInfoResponse) => {
		const errorNumber = getResponseErrorNumber(res.errors);
		if (errorNumber === 3300) {
			navigate(getUrlBasePath(`/link-expired${setRouteLangParam(lang)}`), { replace: true });
		}
		const errorText = getErrorTextFromResponse(res.errors, getLabelValue('mp.generalError', labels));
		setApiError(errorText);
	};

	const { error, data, loading } = useQuery(getMangeOrderCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderManagementCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;
	const teaserItems = currentLabels?.teasers?.items || [];
	const urlOrderHash = getOrderUrlParams(window.location);

	const fetchOrderInfo = async () => {
		setDataLoading(true);

		await OrderService.getOrderInfo(
			{
				orderHash: urlOrderHash,
			},
			lang as Locales,
		).then((res) => {
			if (res.code === 200) {
				const orderDetailsByModuleType = getOrderEntriesSortedByModuleType(res);
				setOrderInfoDetailsByModule(orderDetailsByModuleType);
				updateNewOrderData(res);
				sessionStorage.setItem('orderHash', res.orderInfo.orderHash);
				sessionStorage.setItem('token', res.token);
			} else {
				handleOrderException(res);
			}
		});
		await TollService.getCountries(lang).then((countryData) => {
			updateAllCountries(countryData.countries);
		});
		setDataLoading(false);
		const orderOverviewHash = sessionStorage.getItem('orderOverviewHash');
		if (!orderList.length && orderOverviewHash) {
			await OrderService.getOrderList(
				{
					orderHash: orderOverviewHash,
				},
				lang as Locales,
			).then((res) => {
				if (res.code === 200) {
					updateOrderList(res.orderList);
				}
			});
		}
	};

	useEffect(() => {
		fetchOrderInfo();
	}, [lang, urlOrderHash]);

	useEffect(() => {
		if (orderData) {
			const orderDetailsByModuleType = getOrderEntriesSortedByModuleType(orderData);
			setOrderInfoDetailsByModule(orderDetailsByModuleType);
		}
	}, [orderData]);

	const renderPriceInformation = (buttonClassName = '') => (
		<div className="flex flex-col items-center">
			<Button
				fullWidth
				className={`mt-6 md:flex-shrink md:max-w-[75%] ${buttonClassName}`}
				onClick={() => {
					navigate(getUrlBasePath(`/one-cart/configure${setRouteLangParam(lang)}`));
				}}>
				{getLabelValue('mp.backToStart', labels) || 'Zurück zur Startseite'}
			</Button>
		</div>
	);

	const renderPaymentMethod = () => {
		const canEditPayment = orderData?.orderInfo.changePaymentMethod;
		return (
			<div className="mb-1.5">
				<Typography color="title" className="px-4 mb-3" component="div">
					{currentLabels?.flexPaymentDetails || 'Zahlungsdetails'}
				</Typography>
				<div
					className="flex flex-col justify-between bg-white rounded cursor-pointer ease-in-out duration-500 border border-solid border-transparent hover:border-gray-400"
					data-cy="edit-payment"
					onClick={() =>
						canEditPayment
							? navigate(getUrlBasePath(`/edit-payment-method${setRouteLangParam(lang)}`))
							: () => {}
					}>
					<div className="px-4 py-3.5">
						<Typography variant="title" size="lg" color="title">
							{currentLabels?.flexPaymentMethod || 'Zahlungsart'}
						</Typography>
						<Typography component="p" className="pt-1" size="lg" color="title">
							{orderData?.orderInfo?.selectedPaymentMethod?.name}
						</Typography>
					</div>
					{canEditPayment && (
						<div className="flex gap-1.5 justify-end rounded-b border-solid border-l-0 border-b-0 border-r-0 border-t border-t-gray-600 px-4 py-2.5 hover:bg-[#FAFAFA]">
							<Edit />
							<Typography variant="title" component="div" size="lgEven">
								{currentLabels?.updatePaymentMethod || 'Zahlungsdaten aktualisieren'}
							</Typography>
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderProductOrderSummarySection = (orderInfo: [string, IOrderInfoDetails[]][]) =>
		orderInfo.map(([moduleType, orderDetails], index) => (
			<React.Fragment key={index}>
				{(moduleType === 'Flex' || moduleType === 'Mautbox') && renderPaymentMethod()}
				{moduleType !== 'Mautbox' && (
					<ProductOrderSummarySection moduleType={moduleType} orderDetails={orderDetails} />
				)}
			</React.Fragment>
		));

	const renderProductsInSingleColumn = () => (
		<section className="w-full md:self-center md:w-6/12 [&>*]:mb-5">
			{orderInfoDetailsByModule && !dataLoading ? (
				renderProductOrderSummarySection(orderInfoDetailsByModule)
			) : (
				<SkeletonTemplate type="product" />
			)}
			{renderPriceInformation()}
		</section>
	);

	const renderProductWithTitle = (title: string, orderInfo: [string, IOrderInfoDetails[]][]) => (
		<div className="flex flex-col basis-1/2 gap-4">
			<Typography className="px-4" variant="header" size="xxxlEven" color="title">
				{title}
			</Typography>
			{renderProductOrderSummarySection(orderInfo)}
		</div>
	);

	const renderProductsInTwoColumns = () => {
		const flexOrderInfoDetails = orderInfoDetailsByModule?.filter((order) => order[0] === 'Flex');
		const normalOrderInfoDetails = orderInfoDetailsByModule?.filter((order) => order[0] !== 'Flex');
		return (
			<section className="w-full md:self-center [&>*]:mb-5 md:px-[84px]">
				{dataLoading && <SkeletonTemplate type="product" />}
				{!dataLoading && (
					<div className="flex md:flex-row flex-col md:gap-9 gap-6">
						{normalOrderInfoDetails &&
							renderProductWithTitle(
								currentLabels?.normalProducts || 'Gekaufte Produkte',
								normalOrderInfoDetails,
							)}
						{flexOrderInfoDetails &&
							renderProductWithTitle(
								currentLabels?.flexServices || 'Flex-Services',
								flexOrderInfoDetails,
							)}
					</div>
				)}
				{renderPriceInformation('md:!max-w-[37.5%]')}
			</section>
		);
	};

	const orderHasFlexAndNormalProducts = () =>
		orderData?.orderInfo.flexProduct && orderInfoDetailsByModule && orderInfoDetailsByModule.length > 1;

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper minSpace className="min-h-[calc(100vh-126px)] md:min-h-0">
						<Head
							headline={currentLabels?.headlineIntro || 'Bestellung verwalten'}
							description={
								<div>
									{orderData?.orderInfo.orderNo ? (
										`${currentLabels?.formData?.orderNumber || 'Bestellnummer'}: ${
											orderData.orderInfo.orderNo
										}`
									) : (
										<Skeleton inline width="75%" height="100%" />
									)}
									<br />
									{orderList.length > 1 && (
										<div>
											{currentLabels?.showFurtherOrder || 'Weitere Bestellungen anzeigen:'}{' '}
											{orderList
												.filter(
													({ orderHash }) => orderHash !== getOrderUrlParams(window.location),
												)
												.map(({ orderId, orderNo, orderHash }, i) => {
													const params = createSearchParams();
													params.set('orderHash', orderHash);
													return (
														<>
															{i !== 0 && <span> / </span>}
															<Link
																key={orderId}
																className="text-gray-200"
																to={getUrlBasePath('/order-management', params)}>
																{orderNo}
															</Link>
														</>
													);
												})}
										</div>
									)}
								</div>
							}
							classNameDescription="font-bold"
						/>
						{orderHasFlexAndNormalProducts()
							? renderProductsInTwoColumns()
							: renderProductsInSingleColumn()}
						{apiError && (
							<Toast
								content={apiError}
								icon={<ErrorCrossIcon />}
								onToastFinished={() => setApiError(undefined)}
							/>
						)}
					</Wrapper>
					{teaserItems.length && renderTeasers({ viewport, currentLabels, teaserItems })}
				</>
			)}
		</>
	);
};

export const OrderManagement = withLocalization(OrderManagementPage);
